import React, { useMemo } from 'react';
import Box from '@mui/system/Box';
import isEqual from 'lodash.isequal';

import NRReactGrid from '../NRReactGrid/NRReactGrid';
import { CONSTANTS } from '../../constants/constants';

import { MaturityProvider } from '../NRReactGridCustomComponents/NRCustomProviders/NRMaturityProvider';
import { RemediationStatusProvider } from '../NRReactGridCustomComponents/NRCustomProviders/RemediationStatusProvider/RemediationStatusProvider';
import { TooltipProvider } from '../NRReactGridCustomComponents/NRCustomProviders/TooltipProvider';
import { NRCVEIdProvider } from './VulnerabilitiesTableCustomComponents/NRCVEIdProvider';
import { NRSeverityProvider } from './VulnerabilitiesTableCustomComponents/NRSeverityProvider';
import { NRReachableComponentIconProvider } from './VulnerabilitiesTableCustomComponents/NRReachableComponentIconProvider';

const { ROWS, FILTERS } = CONSTANTS.TABLE.VULNERABILITIES_TABLE;

const columns = [
  {
    title: { value: ROWS.CVE_ID },
    name: 'cve',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.SEVERITY },
    name: 'severity',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.COMPONENT },
    name: 'name',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.VERSION },
    name: 'version',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.FIX_VERSION },
    name: 'fixVersions',
    getCellValue: row => (Array.isArray(row.fixVersions) ? row.fixVersions[0] : row.fixVersions),
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: ROWS.EXPLOIT_MATURITY },
    name: 'maturity',
    hideFilter: true,
    sortDisabled: true
  },
  {
    title: { value: 'Status' },
    name: 'vulnerabilityRemediationStatus',
    hideFilter: true,
    disableEdit: true,
    type: 'multiselectEnum',
    filterValues: FILTERS.VULNERABILITY_REMEDIATION_STATUS_FILTERS,
    getCellValue: row => row.currentRemediation?.status,
    sortDisabled: true
  }
];

const columnExtensions = [
  { columnName: 'cve', width: 174 },
  { columnName: 'name', width: 174 }
];

const PROVIDERS_ARRAY = [
  <NRReachableComponentIconProvider key="ReachableProvider" for={['name']} />,
  <NRCVEIdProvider key="NRCVEIdProvider" for={['cve']} />,
  <NRSeverityProvider key="NRSeverityProvider" for={['severity']} />,
  <MaturityProvider key="MaturityProvider" for={['maturity']} />,
  <RemediationStatusProvider key="RemediationStatusProvider" for={['vulnerabilityRemediationStatus']} />,
  <TooltipProvider
    key="fixVersions"
    for={['fixVersions']}
    extraParams={{
      getCustomTooltipTitle: row => {
        return !!row?.fixVersions?.length ? 'More than one fix version is available—see vulnerability details.' : row?.fixVersions?.[0] || '';
      }
    }}
  />
];

let lastRows = [];
function getRows(vulnerabilities) {
  if (isEqual(vulnerabilities, lastRows)) return lastRows;
  lastRows = vulnerabilities;
  return lastRows;
}

export default function NRRiseAIVulnerabilitiesTable({ vulnerabilities, tableRef, isLoading = false, disabledProviders = [], tableStorageId }) {
  const providersArray = useMemo(() => {
    if (!Array.isArray(disabledProviders) || disabledProviders.length === 0) return PROVIDERS_ARRAY;
    const disabled = new Set(disabledProviders.map(String));
    return PROVIDERS_ARRAY.filter(p => p?.key && !disabled.has(String(p.key)));
  }, [disabledProviders]);

  return (
    <Box>
      <NRReactGrid
        ref={tableRef}
        loading={isLoading}
        rows={getRows(vulnerabilities)}
        columns={columns}
        columnExtensions={columnExtensions}
        providersArray={providersArray}
        hasExtraActions={false}
        hasCursorPagination={false}
        showIntegratedPagination={false}
        showIntegratedSelection={false}
        showSelectAll={false}
        tableStorageId={tableStorageId}
        hideTableHeaderRow={true}
      />
    </Box>
  );
}
